import { useMemo, useState } from 'react';
import { LayoutTemplate, MoreHorizontal, Plus, Printer, Save } from 'lucide-react';
import { sumMoney, formatCurrency } from '@peles/shared';
import type { Event, PlanningItem } from '@peles/shared';
import { FilterSelect } from '../common';
import { EntityModal } from '../common/EntityModal';
import { DropdownMenu } from '../common/DropdownMenu';
import { useEvent } from '../../hooks/useEvents';
import { useExpenseItems } from '../../hooks/useExpenses';
import { useRooms } from '../../hooks/useFloorPlans';
import { useVendorCategories } from '../../hooks/useVendors';
import { useSettingsCatalogItems } from '../../hooks/useSettings';
import { useItemUnits } from '../../hooks/useItemUnits';
import { useRememberedChoice } from '../../hooks/useRememberedChoice';
import { useEventPlanningList, useSavePlanningListAsTemplate } from '../../hooks/usePlanning';
import { PlanningItemModal } from './PlanningItemModal';
import { PlanningTemplatePickerModal } from './PlanningTemplatePickerModal';
import { ItemizedPrintModal } from './ItemizedPrintModal';
import type { ItemizedPrintFilter } from './ItemizedPrintModal';
import { ItemsGrid, PLANNING_ITEM_ATTACHMENT_TYPE } from './ItemsGrid';
import { ITEMS_GROUP_LABELS, lineName, type ItemsGroupBy } from './itemsGridModel';

export { PLANNING_ITEM_ATTACHMENT_TYPE };

export interface ItemizedListFilters {
  categoryIds?: string[];
  roomIds?: string[];
  vendorKeys?: string[];
  expenseItemIds?: string[];
}

interface ItemizedListViewProps {
  eventId: string;
  /** Pre-selected filters (e.g. zoom-in from an expense row). */
  initialFilters?: ItemizedListFilters;
}

type EventWithRelations = Event & {
  eventTypeId?: string;
  eventTypeName?: string;
  venueId?: string | null;
  venueName?: string;
  logoUrl?: string | null;
};

const GROUP_OPTIONS = Object.keys(ITEMS_GROUP_LABELS) as ItemsGroupBy[];

/** Stable filter key for the (possibly id-less) vendor behind an expense line. */
function vendorKey(item: PlanningItem): string | null {
  return item.expenseVendorId ?? item.expenseVendorName ?? null;
}

/**
 * The event's items — everything the needs are made of — as one grid you
 * type into (`ItemsGrid`), with filters by category, room, vendor and need,
 * grouping with subtotals, and the printable report. With exactly one need
 * chosen (a need's own Items tab, or zooming in from an expense row) new items
 * go to it and the Need column is hidden.
 */
export function ItemizedListView({ eventId, initialFilters }: ItemizedListViewProps) {
  const { data: event } = useEvent(eventId);
  const eventRel = event as EventWithRelations | undefined;
  const venueId = eventRel?.venueId ?? null;

  const { data: list, isLoading } = useEventPlanningList(eventId);
  const { data: categories = [] } = useVendorCategories();
  const { data: catalogItems = [] } = useSettingsCatalogItems();
  const { data: rooms = [] } = useRooms(venueId ?? undefined, !!venueId);
  const { data: lines = [] } = useExpenseItems({ eventId });
  const units = useItemUnits();
  const saveAsTemplate = useSavePlanningListAsTemplate();

  const [editor, setEditor] = useState<{ open: boolean; item: PlanningItem | null }>({
    open: false,
    item: null,
  });
  const [templatePickerOpen, setTemplatePickerOpen] = useState(false);
  const [saveTemplateOpen, setSaveTemplateOpen] = useState(false);
  const [printOpen, setPrintOpen] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string[]>(initialFilters?.categoryIds ?? []);
  const [roomFilter, setRoomFilter] = useState<string[]>(initialFilters?.roomIds ?? []);
  const [vendorFilter, setVendorFilter] = useState<string[]>(initialFilters?.vendorKeys ?? []);
  const [lineFilter, setLineFilter] = useState<string[]>(initialFilters?.expenseItemIds ?? []);
  const [groupBy, setGroupBy] = useRememberedChoice<ItemsGroupBy>(
    'items-group-by',
    GROUP_OPTIONS,
    'need',
  );

  // One need chosen: the grid is that need's, and new items go to it.
  const scopedLineId = lineFilter.length === 1 ? lineFilter[0] : undefined;
  const items = useMemo(() => list?.items ?? [], [list]);

  const vendorOptions = useMemo(() => {
    const map = new Map<string, string>();
    for (const item of items) {
      const key = vendorKey(item);
      if (key && !map.has(key)) map.set(key, item.expenseVendorName ?? 'Unknown vendor');
    }
    for (const line of lines) {
      const key = line.vendorId ?? line.vendorName;
      if (key && !map.has(key)) map.set(key, line.vendorName ?? 'Unknown vendor');
    }
    return Array.from(map.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [items, lines]);

  const venueRooms = useMemo(
    () => rooms.filter((r) => r.venueId === venueId).sort((a, b) => a.name.localeCompare(b.name)),
    [rooms, venueId],
  );

  const filtering =
    categoryFilter.length > 0 ||
    roomFilter.length > 0 ||
    vendorFilter.length > 0 ||
    lineFilter.length > 0;

  const filteredItems = useMemo(() => {
    let result = items;
    if (categoryFilter.length > 0) {
      result = result.filter(
        (i) => i.vendorCategoryId && categoryFilter.includes(i.vendorCategoryId),
      );
    }
    if (roomFilter.length > 0) {
      result = result.filter((i) => i.roomId && roomFilter.includes(i.roomId));
    }
    if (vendorFilter.length > 0) {
      result = result.filter((i) => {
        const key = vendorKey(i);
        return key !== null && vendorFilter.includes(key);
      });
    }
    if (lineFilter.length > 0) {
      result = result.filter((i) => i.expenseItemId && lineFilter.includes(i.expenseItemId));
    }
    return result;
  }, [items, categoryFilter, roomFilter, vendorFilter, lineFilter]);

  const grandTotal = useMemo(() => sumMoney(filteredItems.map((i) => i.total)), [filteredItems]);

  // Human-readable summary of the active filters — shown on the printed report.
  const activeFilters: ItemizedPrintFilter[] = useMemo(
    () => [
      {
        label: 'Category',
        values: categoryFilter.map((id) => categories.find((c) => c.id === id)?.name ?? 'Unknown'),
      },
      {
        label: 'Room',
        values: roomFilter.map((id) => rooms.find((r) => r.id === id)?.name ?? 'Unknown'),
      },
      {
        label: 'Vendor',
        values: vendorFilter.map(
          (key) => vendorOptions.find((v) => v.value === key)?.label ?? 'Unknown',
        ),
      },
      {
        label: 'Need',
        values: lineFilter.map((id) => {
          const line = lines.find((l) => l.id === id);
          return line ? lineName(line) : 'Unknown';
        }),
      },
    ],
    [categoryFilter, roomFilter, vendorFilter, lineFilter, categories, rooms, vendorOptions, lines],
  );

  function handleSaveTemplate() {
    if (!list || !templateName.trim()) return;
    saveAsTemplate.mutate(
      { id: list.id, templateName: templateName.trim(), eventTypeId: eventRel?.eventTypeId },
      {
        onSuccess: () => {
          setSaveTemplateOpen(false);
          setTemplateName('');
        },
      },
    );
  }

  const filterBar = (
    <div className="flex flex-wrap items-center gap-2 mb-3">
      <FilterSelect
        label="All Categories"
        items={categories}
        value={categoryFilter}
        onChange={(v) => setCategoryFilter(v as string[])}
        getValue={(c) => c.id}
        getLabel={(c) => c.name}
        multiple
      />
      {venueRooms.length > 0 && (
        <FilterSelect
          label="All Rooms"
          items={venueRooms}
          value={roomFilter}
          onChange={(v) => setRoomFilter(v as string[])}
          getValue={(r) => r.id}
          getLabel={(r) => r.name}
          multiple
        />
      )}
      <FilterSelect
        label="All Vendors"
        items={vendorOptions}
        value={vendorFilter}
        onChange={(v) => setVendorFilter(v as string[])}
        getValue={(o) => o.value}
        getLabel={(o) => o.label}
        multiple
      />
      <FilterSelect
        label="All Needs"
        items={lines}
        value={lineFilter}
        onChange={(v) => setLineFilter(v as string[])}
        getValue={(l) => l.id}
        getLabel={(l) => lineName(l)}
        multiple
      />
      <FilterSelect
        label="Group by"
        items={GROUP_OPTIONS}
        value={groupBy}
        onChange={(v) => setGroupBy((v as ItemsGroupBy) || 'none')}
        getValue={(g) => g}
        getLabel={(g) => `Group: ${ITEMS_GROUP_LABELS[g]}`}
      />
    </div>
  );

  return (
    <>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h3 className="text-base font-semibold text-gray-900">Items</h3>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setEditor({ open: true, item: null })}
            className="min-h-11 sm:min-h-9 px-3 inline-flex items-center gap-1 bg-primary-600 text-white text-sm font-semibold rounded-lg hover:bg-primary-700"
          >
            <Plus size={16} /> Add an item
          </button>
          <DropdownMenu
            align="end"
            ariaLabel="More item actions"
            items={[
              {
                id: 'print',
                label: 'Print…',
                icon: <Printer size={14} />,
                disabled: !eventRel,
                onSelect: () => setPrintOpen(true),
              },
              {
                id: 'template',
                label: 'Use a template',
                icon: <LayoutTemplate size={14} />,
                onSelect: () => setTemplatePickerOpen(true),
              },
              ...(list
                ? [
                    {
                      id: 'save-template',
                      label: 'Save as template',
                      icon: <Save size={14} />,
                      onSelect: () => {
                        setTemplateName(`${eventRel?.name ?? 'Event'} items`);
                        setSaveTemplateOpen(true);
                      },
                    },
                  ]
                : []),
            ]}
            trigger={(props) => (
              <button
                type="button"
                {...props}
                className="min-h-11 sm:min-h-9 px-3 inline-flex items-center gap-1 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50"
              >
                <MoreHorizontal size={16} /> More
              </button>
            )}
          />
        </div>
      </div>

      {filterBar}

      <p className="text-sm text-gray-600 mb-2" data-testid="items-summary">
        {filteredItems.length} item{filteredItems.length === 1 ? '' : 's'} ·{' '}
        <span className="font-semibold text-gray-900">{formatCurrency(grandTotal)}</span>
      </p>

      {isLoading ? (
        <div className="h-24 bg-gray-100 rounded-lg animate-pulse" aria-busy="true" />
      ) : (
        <ItemsGrid
          eventId={eventId}
          items={filteredItems}
          allItems={items}
          lines={lines}
          units={units}
          groupBy={scopedLineId ? 'none' : groupBy}
          lineId={scopedLineId}
          reorderIds={filtering ? undefined : items.map((i) => i.id)}
          onOpen={(item) => setEditor({ open: true, item })}
          onAdd={() => setEditor({ open: true, item: null })}
        />
      )}

      {!isLoading && items.length === 0 && (
        <p className="mt-3 text-sm text-gray-500">
          Nothing itemized yet. Type the first item above, paste rows from a spreadsheet, or{' '}
          <button
            type="button"
            onClick={() => setTemplatePickerOpen(true)}
            className="text-primary-600 underline"
          >
            start from a template
          </button>
          .
        </p>
      )}

      {editor.open && (
        <PlanningItemModal
          key={editor.item?.id ?? 'new'}
          open
          onClose={() => setEditor({ open: false, item: null })}
          item={editor.item}
          eventId={eventId}
          venueId={venueId}
          defaultExpenseItemId={scopedLineId}
          categories={categories}
          catalogItems={catalogItems}
        />
      )}

      <PlanningTemplatePickerModal
        open={templatePickerOpen}
        onClose={() => setTemplatePickerOpen(false)}
        eventId={eventId}
        eventTypeId={eventRel?.eventTypeId}
      />

      {printOpen && eventRel && (
        <ItemizedPrintModal
          event={eventRel}
          items={filteredItems}
          activeFilters={activeFilters}
          attachmentEntityType={PLANNING_ITEM_ATTACHMENT_TYPE}
          onClose={() => setPrintOpen(false)}
        />
      )}

      <EntityModal
        open={saveTemplateOpen}
        onClose={() => setSaveTemplateOpen(false)}
        title="Save as template"
        size="sm"
        footer={
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setSaveTemplateOpen(false)}
              className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSaveTemplate}
              disabled={!templateName.trim() || saveAsTemplate.isPending}
              className="px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:bg-gray-300"
              data-testid="save-template-submit"
            >
              {saveAsTemplate.isPending ? 'Saving…' : 'Save template'}
            </button>
          </div>
        }
      >
        <p className="text-sm text-gray-600 mb-3">
          Each need becomes a template need, with its items, units, prices and tasks. Vendors, rooms
          and who was assigned are left out.
        </p>
        <label
          htmlFor="items-template-name"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Template name
        </label>
        <input
          id="items-template-name"
          type="text"
          value={templateName}
          onChange={(e) => setTemplateName(e.target.value)}
          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
      </EntityModal>
    </>
  );
}
